import { LngLat } from '@breezeventures/ui-kit/map';
import { and, assign, not, or, setup } from 'xstate';

import { ApplicationFormValues } from './form-values';
import { ROUTE_IDS, RouteId, RouteKey } from './route-ids';
import {
  type AgentFormValues,
  type BankAccountFormValues,
  type BusinessAddressFormValues,
  type BusinessDetailsFormValues,
  type BusinessDocumentsFormValues,
  type OfflineStoreFormValues,
  type OfficeAddressFormValues,
  type OnlineStoreFormValues,
  type OwnershipFormValues,
  type StoreAddressFormValues,
  type StorePhotosFormValues,
} from './schemas';
import { offlineStoreDataGuard, StoreData } from './stores-data';

export type PartnerApplicationContext = Partial<ApplicationFormValues> & {
  applicationId?: string;
  agentDraft: AgentFormValues | null;
  storeDraft: Partial<StoreData> | null;
  navigationStack: RouteId[];
};

export type PartnerApplicationEvent =
  | { type: 'ping' }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'ownership.submitData'; ownership: OwnershipFormValues }
  | {
      type: 'businessDetails.submitData';
      businessInfo: BusinessDetailsFormValues;
    }
  | {
      type: 'businessAddress.submitData';
      registeredAddress: BusinessAddressFormValues;
    }
  | {
      type: 'officeAddress.submitData';
      headOfficeAddress: OfficeAddressFormValues;
    }
  | {
      type: 'businessDocuments.submitData';
      businessDocuments: BusinessDocumentsFormValues;
    }
  | {
      type: 'bankDetails.submitData';
      bankDetails: BankAccountFormValues;
    }
  | {
      type: 'storeDetails.submitDataOffline';
      storeData: OfflineStoreFormValues;
    }
  | { type: 'storeDetails.submitDataOnline'; storeData: OnlineStoreFormValues }
  | { type: 'storeAddress.submitData'; storeAddress: StoreAddressFormValues }
  | { type: 'storeMap.submitData'; storeCoordinates: LngLat | null }
  | { type: 'storePhotos.submitData'; storePhotos: StorePhotosFormValues }
  | { type: 'store.add' }
  | { type: 'store.remove'; storeId: string }
  | { type: 'store.edit'; storeId: string }
  | { type: 'storeDraftReady.invalidated' }
  | { type: 'agent.add' }
  | { type: 'agent.edit'; agentId: string }
  | { type: 'agent.remove'; agentId: string }
  | { type: 'agentForm.submitData'; agent: AgentFormValues }
  | { type: 'review.submit' }
  | { type: 'application.created'; applicationId: string }
  | { type: 'xstate.route'; to: RouteId };

export const getRouterGuards = () => {
  return (Object.keys(ROUTE_IDS) as RouteKey[]).map((routeKey) => ({
    guard: ({ context }: { context: PartnerApplicationContext }) => {
      return context.navigationStack.at(-1) === ROUTE_IDS[routeKey];
    },
    target: ROUTE_IDS[routeKey],
  }));
};

export const partnerApplicationMachine = setup({
  types: {
    context: {} as PartnerApplicationContext,
    events: {} as PartnerApplicationEvent,
  },
  guards: {
    hasStores: ({ context }) => !!context.pointsOfSales?.length,
    hasOfflineStores: ({ context }) =>
      !!context.pointsOfSales?.some(offlineStoreDataGuard),
    hasAgents: ({ context }) => !!context.agents?.length,
    hasNavigationStack: ({ context }) => !!context.navigationStack?.length,
  },
  actions: {
    setApplicationId: assign({
      applicationId: ({ event }) =>
        event.type === 'application.created' ? event.applicationId : undefined,
    }),
    submitStore: assign({
      pointsOfSales: ({ context }) => {
        const storeToSubmit = context.storeDraft as StoreData;
        const current = context.pointsOfSales ?? [];

        const existingStoreIndex = current.findIndex(
          (store) => store.details.uiId === storeToSubmit.details.uiId
        );

        if (existingStoreIndex >= 0) {
          const updated = [...current];
          updated[existingStoreIndex] = storeToSubmit;

          return updated;
        }

        return [...current, storeToSubmit];
      },
    }),
    submitAgent: assign({
      agents: ({ context }) => {
        const agentToSubmit = context.agentDraft as AgentFormValues;
        const current = context.agents ?? [];

        const existingAgentIndex = current.findIndex(
          (agent) => agent.uiId === agentToSubmit.uiId
        );

        if (existingAgentIndex >= 0) {
          const updated = [...current];
          updated[existingAgentIndex] = agentToSubmit;

          return updated;
        }

        return [...current, agentToSubmit];
      },
      agentDraft: null,
    }),
    removeStore: assign({
      pointsOfSales: ({ context, event }) => {
        if (event.type === 'store.remove') {
          return (
            context.pointsOfSales?.filter(
              (store) => store.details.uiId !== event.storeId
            ) ?? []
          );
        }
        return context.pointsOfSales;
      },
    }),
    pushToNavigationStack: assign({
      navigationStack: ({ context }, params: { route: RouteId }) => {
        if (
          context.navigationStack.includes(ROUTE_IDS.review) &&
          params.route !== context.navigationStack.at(-1)
        ) {
          return [...context.navigationStack, params.route];
        }
        return context.navigationStack;
      },
    }),
    popFromNavigationStack: assign({
      navigationStack: ({ context }) => {
        return context.navigationStack.slice(0, -1);
      },
    }),
  },
}).createMachine({
  id: 'partnerApplication',
  initial: 'businessBlock',
  context: {
    storeDraft: null,
    agentDraft: null,
    navigationStack: [],
  },
  states: {
    router: {
      entry: {
        type: 'popFromNavigationStack',
      },
      always: getRouterGuards(),
    },
    businessBlock: {
      on: {
        'application.created': { actions: 'setApplicationId' },
      },
      initial: 'businessStart',
      states: {
        businessStart: {
          on: { next: { target: 'ownership' } },
        },
        ownership: {
          id: 'ownership',
          route: {},
          on: {
            'ownership.submitData': {
              actions: assign({
                ownership: ({ event }) => event.ownership,
              }),
            },
            next: [
              {
                guard: ({ context }) =>
                  context.ownership?.type === 'INDIVIDUAL',
                target: 'businessDetails',
              },
              { target: 'continueAirtable' },
            ],
            back: {
              target: 'businessStart',
            },
          },
        },
        continueAirtable: {
          on: {
            back: { target: 'ownership' },
          },
        },
        businessDetails: {
          id: 'businessDetails',
          route: {},
          entry: {
            type: 'pushToNavigationStack',
            params: {
              route: ROUTE_IDS.businessDetails,
            },
          },
          on: {
            'businessDetails.submitData': {
              actions: assign({
                businessInfo: ({ event }) => event.businessInfo,
              }),
            },
            next: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              { target: 'businessAddress' },
            ],
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: 'ownership',
              },
            ],
          },
        },
        businessAddress: {
          id: 'businessAddress',
          route: {},
          entry: {
            type: 'pushToNavigationStack',
            params: {
              route: ROUTE_IDS.businessAddress,
            },
          },
          on: {
            'businessAddress.submitData': {
              actions: assign({
                registeredAddress: ({ event }) => event.registeredAddress,
              }),
            },
            next: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              { target: 'officeAddress' },
            ],
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: 'businessDetails',
              },
            ],
          },
        },
        officeAddress: {
          id: 'officeAddress',
          route: {},
          entry: {
            type: 'pushToNavigationStack',
            params: {
              route: ROUTE_IDS.officeAddress,
            },
          },
          on: {
            'officeAddress.submitData': {
              actions: assign({
                headOfficeAddress: ({ event }) => event.headOfficeAddress,
              }),
            },
            next: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              { target: 'businessDocuments' },
            ],
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: 'businessAddress',
              },
            ],
          },
        },
        businessDocuments: {
          id: 'businessDocuments',
          route: {},
          entry: {
            type: 'pushToNavigationStack',
            params: {
              route: ROUTE_IDS.businessDocuments,
            },
          },
          on: {
            'businessDocuments.submitData': {
              actions: assign({
                businessDocuments: ({ event }) => event.businessDocuments,
              }),
            },
            next: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              { target: 'bankDetails' },
            ],
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: 'officeAddress',
              },
            ],
          },
        },
        bankDetails: {
          id: 'bankDetails',
          route: {},
          entry: {
            type: 'pushToNavigationStack',
            params: {
              route: ROUTE_IDS.bankDetails,
            },
          },
          on: {
            'bankDetails.submitData': {
              actions: assign({
                bankDetails: ({ event }) => event.bankDetails,
              }),
            },
            next: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              { target: '#partnerApplication.storesBlock' },
            ],
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: 'businessDocuments',
              },
            ],
          },
        },
      },
    },
    storesBlock: {
      id: 'stores',
      route: {},
      initial: 'storesStart',
      entry: {
        type: 'pushToNavigationStack',
        params: {
          route: ROUTE_IDS.stores,
        },
      },
      states: {
        storesStart: {
          always: {
            guard: 'hasStores',
            target: 'storesList',
          },
          on: {
            next: [
              {
                guard: not('hasStores'),
                target: 'storeEdit.storeDetails',
              },
              { target: 'storesList' },
            ],
            back: {
              target: '#partnerApplication.businessBlock.bankDetails',
            },
          },
        },
        storesList: {
          always: {
            guard: not('hasStores'),
            target: 'storesStart',
          },
          on: {
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: '#partnerApplication.businessBlock.bankDetails',
              },
            ],
            'store.add': {
              target: 'storeEdit',
              actions: assign({ storeDraft: null }),
            },
            'store.edit': {
              target: 'storeEdit.storeDetails',
              actions: assign({
                storeDraft: ({ context, event }) => {
                  const store = context.pointsOfSales?.find(
                    (s) => s.details.uiId === event.storeId
                  );
                  return store ? { ...store } : null;
                },
              }),
            },
            'store.remove': [
              {
                guard: ({ context }) =>
                  context.pointsOfSales?.filter(offlineStoreDataGuard)
                    .length === 1,
                actions: ['removeStore', assign({ agents: [] })],
              },
              {
                actions: ['removeStore'],
              },
            ],
            next: [
              {
                guard: and([
                  'hasNavigationStack',
                  'hasOfflineStores',
                  not('hasAgents'),
                ]),
                target: '#partnerApplication.agentsBlock',
              },
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.review',
              },
              {
                guard: or(['hasOfflineStores', 'hasAgents']),
                target: '#partnerApplication.agentsBlock',
              },
              {
                target: '#partnerApplication.review',
              },
            ],
          },
        },
        storeEdit: {
          initial: 'storeDetails',
          states: {
            storeDetails: {
              on: {
                back: {
                  target: '#partnerApplication.storesBlock.storesList',
                  actions: assign({ storeDraft: null }),
                },
                'storeDetails.submitDataOffline': {
                  target: 'storeAddress',
                  actions: assign({
                    storeDraft: ({ context, event }) => {
                      return {
                        ...context.storeDraft,
                        details: event.storeData,
                      };
                    },
                  }),
                },
                'storeDetails.submitDataOnline': {
                  target: 'storeDraftReady',
                  actions: assign({
                    storeDraft: ({ context, event }) => {
                      return {
                        ...context.storeDraft,
                        details: event.storeData,
                      };
                    },
                  }),
                },
              },
            },
            storeAddress: {
              on: {
                back: { target: 'storeDetails' },
                'storeAddress.submitData': {
                  target: 'storeMap',
                  actions: assign({
                    storeDraft: ({ context, event }) => {
                      return {
                        ...context.storeDraft,
                        address: event.storeAddress,
                      };
                    },
                  }),
                },
              },
            },
            storeMap: {
              on: {
                back: { target: 'storeAddress' },
                'storeMap.submitData': {
                  target: 'storePhotos',
                  actions: assign({
                    storeDraft: ({ context, event }) => {
                      return {
                        ...context.storeDraft,
                        coordinates: event.storeCoordinates,
                      };
                    },
                  }),
                },
              },
            },
            storePhotos: {
              on: {
                back: { target: 'storeMap' },
                'storePhotos.submitData': {
                  target: 'storeDraftReady',
                  actions: assign({
                    storeDraft: ({ context, event }) => {
                      return {
                        ...context.storeDraft,
                        photos: event.storePhotos,
                      };
                    },
                  }),
                },
              },
            },
            storeDraftReady: {
              entry: 'submitStore',
              on: {
                'storeDraftReady.invalidated': [
                  {
                    guard: ({ context }) =>
                      context.storeDraft?.details?.type === 'ONLINE',
                    target: 'storeDetails',
                  },
                  {
                    target: 'storePhotos',
                  },
                ],
                next: {
                  actions: assign({ storeDraft: null }),
                  target: '#partnerApplication.storesBlock.storesList',
                },
              },
            },
          },
        },
      },
    },
    agentsBlock: {
      id: 'agents',
      route: {},
      entry: {
        type: 'pushToNavigationStack',
        params: {
          route: ROUTE_IDS.agents,
        },
      },
      on: {
        'application.created': { actions: 'setApplicationId' },
      },
      initial: 'agentsStart',
      states: {
        agentsStart: {
          always: {
            guard: 'hasAgents',
            target: 'agentsList',
          },
          on: {
            next: [
              {
                guard: not('hasAgents'),
                target: 'agentEdit.agentForm',
                actions: assign({ agentDraft: null }),
              },
              { target: 'agentsList' },
            ],
            back: {
              target: '#partnerApplication.storesBlock.storesList',
            },
          },
        },
        agentsList: {
          always: {
            guard: not('hasAgents'),
            target: 'agentsStart',
          },
          on: {
            back: [
              {
                guard: 'hasNavigationStack',
                target: '#partnerApplication.router',
              },
              {
                target: '#partnerApplication.storesBlock.storesList',
              },
            ],
            'agent.add': {
              target: 'agentEdit.agentForm',
              actions: assign({ agentDraft: null }),
            },
            'agent.edit': {
              target: 'agentEdit.agentForm',
              actions: assign({
                agentDraft: ({ context, event }) => {
                  const agentToEdit = context.agents?.find(
                    (agent) => agent.uiId === event.agentId
                  );
                  return agentToEdit ? { ...agentToEdit } : null;
                },
              }),
            },
            'agent.remove': {
              actions: assign({
                agents: ({ context, event }) =>
                  context.agents?.filter(
                    (agent) => agent.uiId !== event.agentId
                  ) ?? [],
              }),
            },
            next: [
              {
                target: '#partnerApplication.review',
              },
            ],
          },
        },
        agentEdit: {
          initial: 'agentForm',
          states: {
            agentForm: {
              on: {
                back: {
                  target: '#partnerApplication.agentsBlock.agentsList',
                  actions: assign({ agentDraft: null }),
                },
                'agentForm.submitData': {
                  actions: [
                    assign({
                      agentDraft: ({ event }) => event.agent,
                    }),
                    'submitAgent',
                  ],
                },
                next: {
                  target: '#partnerApplication.agentsBlock.agentsList',
                },
              },
            },
          },
        },
      },
    },
    review: {
      id: 'review',
      route: {},
      entry: assign({
        navigationStack: [ROUTE_IDS.review],
      }),
      on: {
        back: [
          {
            guard: 'hasOfflineStores',
            actions: {
              type: 'popFromNavigationStack',
            },
            target: '#partnerApplication.agentsBlock',
          },
          {
            actions: {
              type: 'popFromNavigationStack',
            },
            target: '#partnerApplication.storesBlock',
          },
        ],
      },
    },
    submitted: {
      type: 'final',
    },
  },
});
